/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { authClientsSetIssuanceProfile } from "../funcs/authClientsSetIssuanceProfile.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { IssuanceProfile } from "../models/components/issuanceprofile.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  SetIssuanceProfileRequest,
  SetIssuanceProfileSecurity,
} from "../models/operations/setissuanceprofile.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type SetIssuanceProfileMutationVariables = {
  request: SetIssuanceProfileRequest;
  security?: SetIssuanceProfileSecurity | undefined;
  options?: RequestOptions;
};

export type SetIssuanceProfileMutationData = IssuanceProfile;

export type SetIssuanceProfileMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * setIssuanceProfile authClients
 *
 * @remarks
 * Create or replace the issuance profile of one headless caller signing in through a sign-in: an OAuth client, or a workload issuer and subject. Each choice names a credential the server offers, or none for a server that needs no upstream authentication. A workload subject represents no person, so its profile names shared credentials only. Choices take effect on sessions issued afterwards; existing sessions keep theirs. Choosing a credential grants nothing: each issuance checks the caller may use it. Takes effect only where typed auth client resolution is enforced. Requires org:admin.
 */
export function useSetIssuanceProfileMutation(
  options?: MutationHookOptions<
    SetIssuanceProfileMutationData,
    SetIssuanceProfileMutationError,
    SetIssuanceProfileMutationVariables
  >,
): UseMutationResult<
  SetIssuanceProfileMutationData,
  SetIssuanceProfileMutationError,
  SetIssuanceProfileMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildSetIssuanceProfileMutation(client, options),
    ...options,
  });
}

export function mutationKeySetIssuanceProfile(): MutationKey {
  return ["@gram/client", "authClients", "setIssuanceProfile"];
}

export function buildSetIssuanceProfileMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: SetIssuanceProfileMutationVariables,
  ) => Promise<SetIssuanceProfileMutationData>;
} {
  return {
    mutationKey: mutationKeySetIssuanceProfile(),
    mutationFn: function setIssuanceProfileMutationFn({
      request,
      security,
      options,
    }): Promise<SetIssuanceProfileMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(authClientsSetIssuanceProfile(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
